import React, { useState, useEffect, useRef } from "react";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../firebase-config";

const COUNCILS = [
  "Barking and Dagenham",
  "Bexley",
  "Brent",
  "Bromley",
  "Camden",
  "City of London",
  "Croydon",
  "Greenwich",
  "Hackney",
  "Hammersmith and Fulham",
  "Haringey",
  "Havering",
  "Hillingdon",
  "Islington",
  "Kensington and Chelsea",
  "Lewisham",
  "Newham",
  "Richmond upon Thames",
  "Southwark",
  "Tower Hamlets",
  "Westminster",
];

const ITEM_TYPE_LABELS = {
  ANF: "Adult Non-Fiction",
  AF: "Adult Fiction",
  CNF: "Children's Non-Fiction",
  CF: "Children's Fiction",
  TNF: "Teen Non-Fiction",
  TF: "Teen Fiction",
  REF: "Reference",
};

function SearchPanel({ initialBranch, initialCouncil }) {
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterCouncil, setFilterCouncil] = useState(initialCouncil || "");
  const [filterBranch, setFilterBranch] = useState(initialBranch || "");
  const [filterScope, setFilterScope] = useState(
    initialBranch ? "branch" : initialCouncil ? "council" : null,
  );
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("title");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const prevCouncilRef = useRef(initialCouncil || "");

  useEffect(() => {
    const prevCouncil = prevCouncilRef.current;
    prevCouncilRef.current = filterCouncil;

    if (!filterCouncil) {
      setBranchOptions([]);
      if (prevCouncil !== filterCouncil) setFilterBranch("");
      return;
    }
    setBranchesLoading(true);
    if (prevCouncil !== filterCouncil) setFilterBranch("");
    getDocs(
      query(
        collection(db, "library_branches"),
        where("council", "==", filterCouncil),
      ),
    )
      .then((snap) => {
        const names = snap.docs.map((d) => d.data().branch_name).sort();
        setBranchOptions(names);
      })
      .catch(console.error)
      .finally(() => setBranchesLoading(false));
  }, [filterCouncil]);

  const applyLocationFilter = () => {
    if (filterBranch) {
      setFilterScope("branch");
    } else if (filterCouncil) {
      setFilterScope("council");
    }
    setFilterPanelOpen(false);
  };

  const clearFilter = () => {
    setFilterCouncil("");
    setFilterBranch("");
    setFilterScope(null);
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = searchQuery.trim().toLowerCase();
    if (!q) return;

    setLoading(true);
    setSearched(true);
    setResults([]);

    const type = searchType.toLowerCase();

    try {
      let firestoreQuery;
      let extraTokens = [];

      if (type === "isbn") {
        firestoreQuery = query(
          collection(db, "library_books"),
          where("isbn13", "==", q.replace(/[-\s]/g, "")),
          limit(20),
        );
      } else if (type === "title") {
        firestoreQuery = query(
          collection(db, "library_books"),
          where("title_tokens", "array-contains", q),
          limit(20),
        );
      } else {
        // Split "Gaar Adams" → ["gaar", "adams"]; query first token, filter by rest
        const tokens = q.split(/[\s,]+/).filter(Boolean);
        extraTokens = tokens.slice(1);
        firestoreQuery = query(
          collection(db, "library_books"),
          where("author_tokens", "array-contains", tokens[0]),
          limit(50),
        );
      }

      const snap = await getDocs(firestoreQuery);
      let docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (extraTokens.length > 0) {
        docs = docs.filter((book) =>
          extraTokens.every((token) => book.author_tokens?.includes(token)),
        );
      }

      setResults(docs);
    } catch (err) {
      console.error("Search error:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = results.filter((book) => {
    if (!filterScope) return true;
    if (filterScope === "branch" && filterBranch) {
      return book.branches?.some((b) => b.branch_name === filterBranch);
    }
    if (filterScope === "council" && filterCouncil) {
      return book.branches?.some((b) => b.council === filterCouncil);
    }
    return true;
  });

  const placeholder =
    searchType === "isbn"
      ? "e.g. 9780747532743"
      : searchType === "author"
        ? "e.g. Shakespeare"
        : "e.g. Othello";

  return (
    <div className="search-panel">
      <form className="search-form" onSubmit={handleSearch}>
        <div className="search-type-tabs">
          {["Title", "Author", "ISBN"].map((type) => (
            <button
              key={type}
              type="button"
              className={`type-tab ${searchType === type ? "active" : ""}`}
              onClick={() => {
                setSearchType(type);
                setSearchQuery("");
                setResults([]);
                setSearched(false);
              }}
            >
              {type}
            </button>
          ))}
          <button
            type="button"
            className={`type-tab filter-toggle-tab ${filterPanelOpen ? "active" : ""} ${filterScope ? "filter-has-value" : ""}`}
            onClick={() => setFilterPanelOpen((o) => !o)}
          >
            📍{" "}
            {filterScope === "branch"
              ? filterBranch
              : filterScope === "council"
                ? filterCouncil
                : "Location"}
          </button>
        </div>
        <div className="search-input-row">
          <input
            className="search-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={placeholder}
          />
          <button
            className="search-btn"
            type="submit"
            disabled={loading || !searchQuery.trim()}
          >
            {loading ? "…" : "Search"}
          </button>
        </div>
      </form>

      {filterPanelOpen && (
        <div className="filter-panel">
          <div className="filter-panel-row">
            <select
              className="filter-select"
              value={filterCouncil}
              onChange={(e) => setFilterCouncil(e.target.value)}
            >
              <option value="">All of London</option>
              {COUNCILS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterBranch}
              onChange={(e) => setFilterBranch(e.target.value)}
              disabled={!filterCouncil || branchesLoading}
            >
              <option value="">
                {branchesLoading
                  ? "Loading…"
                  : filterCouncil
                    ? "All branches"
                    : "Select a council first"}
              </option>
              {branchOptions.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-panel-actions">
            <button
              type="button"
              className="filter-apply-btn"
              onClick={applyLocationFilter}
              disabled={!filterCouncil}
            >
              Apply
            </button>
            {filterScope && (
              <button
                type="button"
                className="filter-panel-clear-btn"
                onClick={clearFilter}
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      )}

      {filterScope && (
        <div className="search-filter-banner">
          <span className="filter-location">
            📍 {filterScope === "branch" ? filterBranch : filterCouncil}
          </span>
          <div className="filter-actions">
            {filterBranch && filterCouncil && filterScope === "branch" && (
              <button
                className="filter-scope-btn"
                onClick={() => setFilterScope("council")}
              >
                Expand to {filterCouncil}
              </button>
            )}
            {filterScope === "council" && filterBranch && (
              <button
                className="filter-scope-btn"
                onClick={() => setFilterScope("branch")}
              >
                {filterBranch} only
              </button>
            )}
            <button
              className="filter-clear-btn"
              onClick={clearFilter}
              aria-label="Clear filter"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="search-loading">
          <div className="loading-spinner" />
          <p>Searching across London libraries…</p>
        </div>
      )}

      {!loading && searched && filteredResults.length === 0 && (
        <div className="no-results">
          <p>No books found for "{searchQuery}"</p>
          <p className="no-results-hint">
            {searchType === "isbn"
              ? "Try the full 13-digit ISBN without dashes."
              : "Try a shorter search term or switch to a different search type."}
          </p>
        </div>
      )}

      <div className="results-list">
        {filteredResults.map((book) => (
          <div key={book.id} className="result-card">
            <div className="result-book-info">
              <h3 className="result-title">
                {book.isbn13 ? (
                  <a
                    href={`https://openlibrary.org/isbn/${book.isbn13}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {book.title}
                  </a>
                ) : (
                  book.title
                )}
              </h3>
              <p className="result-author">{book.author}</p>
              <div className="result-meta">
                {book.rating != null && (
                  <span className="book-rating">
                    ★ {Number(book.rating).toFixed(1)}
                  </span>
                )}
                {book.genre && <span className="book-genre">{book.genre}</span>}
                {book.year_published && (
                  <span className="book-year">{book.year_published}</span>
                )}
                {book.item_type && (
                  <span className="book-genre">
                    {ITEM_TYPE_LABELS[book.item_type] || book.item_type}
                  </span>
                )}
                <span className="total-copies">
                  {book.total_copies}{" "}
                  {book.total_copies === 1 ? "copy" : "copies"}
                </span>
              </div>
            </div>
            <div className="result-branches">
              <h4>Available at:</h4>
              {(book.branches || []).map((b, i) => (
                <div key={i} className="branch-availability">
                  <span className="availability-branch">{b.branch_name}</span>
                  <span className="availability-council">{b.council}</span>
                  <span className="availability-copies">
                    {b.copies} {b.copies === 1 ? "copy" : "copies"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SearchPanel;
